import { onboardUser } from "@/api/users";
import Dropdown from "@/components/form/Dropdown";
import NumberInput from "@/components/form/NumberInput";
import TextInput from "@/components/form/TextInput";
import { Button } from "@/components/ui/button";
import { PaymentInfo } from "@/models/payment-info.model";
import { User } from "@/models/user.model";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { userInfo } from "os";
import React, { useEffect } from "react";

export const cpfTypeOptions = [
  {
    label: "NONE",
    value: "NONE",
  },
  {
    label: "Singapore Pr 2 Year",
    value: "SINGAPORE PR 2 YEAR",
  },
  {
    label: "Singapore Pr 1 Year",
    value: "SINGAPORE PR 1 YEAR",
  },
  {
    label: "Singapore Citizen, Pr 3 Year And Above",
    value: "SINGAPORE CITIZEN, PR 3 YEAR AND ABOVE",
  },
];
export const cpfSectionOptions = [
  { label: "NONE", value: "NONE" },
  { label: "CPF1", value: "CPF1" },
  { label: "CPF2", value: "CPF2" },
];

export const selfHelpGroupOptions = [
  { label: "NONE", value: "NONE" },
  { label: "CDAC", value: "CDAC" },
  { label: "SINDA", value: "SINDA" },
  { label: "MBMF", value: "MBMF" },
  { label: "ECF", value: "ECF" },
];

export const bankNameOptions = [
  { label: "", value: "NONE" },
  {
    label: "DBS Bank",
    value: "DBS BANK",
  },
  {
    label: "POSB Bank",
    value: "POSB BANK",
  },
  {
    label: "Oversea-Chinese Banking Corporation",
    value: "OVERSEA-CHINESE BANKING CORPORATION",
  },
  {
    label: "United Overseas Bank",
    value: "UNITED OVERSEAS BANK",
  },
  {
    label: "Malayan Banking Berhad",
    value: "MALAYAN BANKING BERHAD",
  },
  {
    label: "Citibank",
    value: "CITIBANK",
  },
  {
    label: "Standard Chartered Bank",
    value: "STANDARD CHARTERED BANK",
  },
  {
    label: "The Hongkong And Shanghai Banking Corporation",
    value: "THE HONGKONG AND SHANGHAI BANKING CORPORATION",
  },
  {
    label: "RHB Bank",
    value: "RHB BANK",
  },
  {
    label: "Australia & New Zealand Bank",
    value: "AUSTRALIA & NEW ZEALAND BANK",
  },
  {
    label: "Bank Of China",
    value: "BANK OF CHINA",
  },
  {
    label: "CIMB Bank",
    value: "CIMB BANK",
  },
];

interface Props {
  setPage: React.Dispatch<React.SetStateAction<number>>;
  paymentInfo: PaymentInfo | null;
  setPaymentInfo: React.Dispatch<React.SetStateAction<PaymentInfo | null>>;
}
function PaymentInfoForm({ setPage, paymentInfo, setPaymentInfo }: Props) {
  const [basicSalary, setBasicSalary] = React.useState<number>(
    paymentInfo?.basicSalary || 0
  );
  const [employeeCpfContributionRate, setEmployeeCpfContributionRate] =
    React.useState<number>(paymentInfo?.employeeCpfContributionRate || 0);

  const [employerCpfContributionRate, setEmployerCpfContributionRate] =
    React.useState<number>(paymentInfo?.employerCpfContributionRate || 0);

  const [cpfType, setCpfType] = React.useState<string>(
    paymentInfo?.cpfType || "NONE"
  );
  const [cpfSection, setCpfSection] = React.useState<string>(
    paymentInfo?.cpfSection || "NONE"
  );
  const [selfHelpGroup, setSelfHelpGroup] = React.useState<string>(
    paymentInfo?.selfHelpGroup || "NONE"
  );
  const [bankName, setBankName] = React.useState<string>(
    paymentInfo?.bankName || "NONE"
  );
  const [bankCode, setBankCode] = React.useState<string>(
    paymentInfo?.bankCode || ""
  );
  const [branchCode, setBranchCode] = React.useState<string>(
    paymentInfo?.branchCode || ""
  );
  const [accountNo, setAccountNo] = React.useState<string>(
    paymentInfo?.accountNo || ""
  );
  const [err, setErr] = React.useState<string>("");
  const handleBack = () => {
    setPage((prev) => prev - 1);
  };
  const isValid = () => {
    if (!basicSalary || basicSalary === 0) {
      setErr("Salary must be more than 0.");
      return false;
    }
    if (
      isNaN(employeeCpfContributionRate) ||
      isNaN(employerCpfContributionRate) ||
      cpfType.length === 0 ||
      cpfSection.length === 0 ||
      selfHelpGroup.length === 0
    ) {
      setErr("Please fill in the relevant CPF fields.");
      return false;
    }
    console.log("no error");
    setErr("");
    return true;
  };

  useEffect(() => {
    if (isValid()) {
      setPaymentInfo({
        basicSalary,
        employeeCpfContributionRate,
        employerCpfContributionRate,
        cpfType: cpfType === "NONE" ? undefined : cpfType,
        cpfSection: cpfSection === "NONE" ? undefined : cpfSection,
        selfHelpGroup: selfHelpGroup === "NONE" ? undefined : selfHelpGroup,
        bankName: bankName === "NONE" ? "" : bankName,
        bankCode: bankCode || "",
        branchCode: branchCode || "",
        accountNo: accountNo || "",
      });
    }
  }, [
    employeeCpfContributionRate,
    employerCpfContributionRate,
    basicSalary,
    cpfType,
    cpfSection,
    selfHelpGroup,
    bankName,
    bankCode,
    branchCode,
    accountNo,
  ]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid()) {
      return;
    }
    setPage((prev) => prev + 1);
    setErr("");
  };

  return (
    <form className="w-full h-max gap-4 flex flex-col" onSubmit={handleSubmit}>
      <div className="w-full h-full gap-5 flex">
        <div className="w-full h-full flex flex-col gap-4">
          <div className="font-bold">Employment Details</div>
          <NumberInput
            label="Basic Salary *"
            value={basicSalary}
            setValue={setBasicSalary}
          />
          <div className="w-full flex gap-4">
            <NumberInput
              label="Employee CPF Contribution Rate (%) *"
              value={employeeCpfContributionRate}
              setValue={setEmployeeCpfContributionRate}
            />
            <NumberInput
              label="Employer CPF Contribution Rate (%) *"
              value={employerCpfContributionRate}
              setValue={setEmployerCpfContributionRate}
            />
          </div>

          <Dropdown
            items={cpfTypeOptions}
            label="CPF Type *"
            selectedString={cpfType}
            setSelectedString={setCpfType}
          />
          <Dropdown
            label="CPF Section *"
            items={cpfSectionOptions}
            selectedString={cpfSection}
            setSelectedString={setCpfSection}
          />
          <Dropdown
            label="Self Help Group *"
            items={selfHelpGroupOptions}
            selectedString={selfHelpGroup}
            setSelectedString={setSelfHelpGroup}
          />
        </div>
        <div className="w-full h-full flex flex-col gap-4">
          <div>
            <div className="font-bold">Bank Details</div>
            <div className="text-gray-500 text-sm">
              Optionally, allow employee to fill in his/her own bank details.
            </div>
          </div>

          <Dropdown
            items={bankNameOptions}
            label="Bank Name"
            selectedString={bankName}
            setSelectedString={setBankName}
          />
          <TextInput
            label="Bank Code"
            value={bankCode}
            setValue={setBankCode}
            type="text"
          />
          <TextInput
            label="Branch Code"
            value={branchCode}
            setValue={setBranchCode}
            type="text"
          />
          <TextInput
            label="Account Number"
            value={accountNo}
            setValue={setAccountNo}
            type="text"
          />
        </div>
      </div>
      {err && <div className="text-red-500 text-sm">{err}</div>}

      <div className="flex w-full justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => setPage((prev) => prev - 1)}
        >
          Previous
        </Button>
        <Button type="submit" disabled={err.length > 0}>
          <ChevronRight />
        </Button>
      </div>
    </form>
  );
}

export default PaymentInfoForm;
